import React, { useCallback } from 'react';
import { FormShowTemplate } from 'framework/form';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import {
  calculateEnhancedTeamStructure,
  useProjectTeamService,
  useProjectDataContext,
  TeamType,
} from 'domain/shared/newProject';
import { useTeam } from '../hooks/useTeam';
import { Loading } from 'framework/ui/mui-builder';
import ProjectTeamContentWrapper from './ProjectHeatmap/ProjectTeamContentWrapper';

type TeamBudgetProject = { budget?: { billingType?: string; startDate?: string; endDate?: string } };

const buildTeamFormData = (source: TeamType | undefined, project: TeamBudgetProject) => {
  const allocationByAssignmentId = new Map(
    (source?.assignments ?? []).map(assignment => [String(assignment.id), assignment]),
  );

  const teamWithAllocation = source?.team && {
    ...source.team,
    members: (source.team.members ?? []).map(member => {
      const allocation = allocationByAssignmentId.get(String(member.assignmentId));
      return { ...member, minPercent: allocation?.minPercent ?? null, maxPercent: allocation?.maxPercent ?? null };
    }),
  };

  return {
    ...(source || {}),
    ...calculateEnhancedTeamStructure(
      teamWithAllocation || source?.team,
      source?.budget?.fte,
      project.budget?.billingType,
    ),
    budget: {
      ...(source?.budget || {}),
      ...(project?.budget?.startDate && project?.budget?.endDate
        ? { startDate: project.budget.startDate, endDate: project.budget.endDate }
        : {}),
    },
  };
};

const ProjectTeamLayout = () => {
  const { createProjectTeam, updateProjectTeam, getProjectTeamValidationSchema } = useProjectTeamService();
  const { data: project } = useProjectDataContext();
  const { data, isDataReloading } = useTeam(project.id);

  const getDataFunc = useCallback(() => Promise.resolve(buildTeamFormData(data, project)), [data, project]);

  const submitFormFunc = useCallback(
    async (formData: FormFieldsData) => {
      const requestBody = {
        id: formData.team.id,
        projectId: project.id,
        location: formData.team.location,
        type: formData.team.type,
        expectedStructure: formData.team.expectedStructure,
      };

      const submitFunc = formData.isNeedCreateTeam ? createProjectTeam : updateProjectTeam;
      const savedTeam = await submitFunc(requestBody);

      if (!savedTeam?.id) {
        throw new Error();
      }

      return buildTeamFormData({ ...(data ?? {}), team: savedTeam } as TeamType, project);
    },
    [createProjectTeam, updateProjectTeam, project, data],
  );

  if (isDataReloading) {
    return <Loading isInitialPosition />;
  }

  return (
    <FormShowTemplate
      isInitialsLoadingPosition
      getValidationSchemaFunc={getProjectTeamValidationSchema}
      submitFormFunc={submitFormFunc}
      afterSubmit={{
        message: 'The team have been successfully updated.',
      }}
      getDataFunc={getDataFunc}
    >
      <ProjectTeamContentWrapper />
    </FormShowTemplate>
  );
};

export default ProjectTeamLayout;
